/**
 * Layout-shift-resilient hash scrolling.
 *
 * Below-fold homepage sections are SSR'd directly (Homepage V5 PR-D Task D1),
 * so most anchor jumps land correctly on the first paint. This still guards
 * against any residual reflow above the target (client-hydrating widgets,
 * font swap, etc.) by gliding to the target, then *locking* onto it with
 * instant corrections (driven by rAF + a ResizeObserver) until the layout
 * is stable.
 *
 * Honors prefers-reduced-motion (single instant jump, no settle loop).
 */

interface ScrollToHashOptions {
  /** Pixels reserved at the top for the fixed pill navbar. */
  offset?: number;
  /** Max time to keep re-aligning while deferred content settles (ms). */
  maxSettleMs?: number;
}

const DEFAULT_OFFSET = 96;
const DEFAULT_MAX_SETTLE_MS = 2500;
/** Don't fight the initial smooth animation before this elapses (ms). */
const SMOOTH_GRACE_MS = 450;
/** Drift below this (px) counts as "on target". */
const DRIFT_TOLERANCE = 2;
/** Stay on target for this long (ms) before declaring the page settled. */
const STABLE_HOLD_MS = 300;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Scroll the element matching `hash` to just below the fixed navbar, keeping it
 * aligned as the page above it reflows. `hash` may be "#pricing" or "pricing".
 */
export function scrollToHashWithSettle(
  hash: string,
  { offset = DEFAULT_OFFSET, maxSettleMs = DEFAULT_MAX_SETTLE_MS }: ScrollToHashOptions = {},
): void {
  if (typeof window === 'undefined') return;
  const id = hash.replace(/^#/, '');
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;

  const targetTop = () => window.scrollY + el.getBoundingClientRect().top - offset;

  if (prefersReducedMotion()) {
    window.scrollTo({ top: targetTop(), behavior: 'auto' });
    return;
  }

  // Glide toward the current (still-growing) target.
  window.scrollTo({ top: targetTop(), behavior: 'smooth' });

  const start = performance.now();
  let raf = 0;
  let resizeObserver: ResizeObserver | null = null;
  let stableSince = 0;

  const cleanup = () => {
    cancelAnimationFrame(raf);
    resizeObserver?.disconnect();
    resizeObserver = null;
  };

  // Instantly re-lock when content above the target expands (the main cause of drift).
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (performance.now() - start < SMOOTH_GRACE_MS) return;
      if (Math.abs(targetTop() - window.scrollY) > DRIFT_TOLERANCE) {
        window.scrollTo({ top: targetTop(), behavior: 'auto' });
      }
    });
    resizeObserver.observe(document.body);
  }

  const tick = (now: number) => {
    const drift = Math.abs(targetTop() - window.scrollY);

    // Once the initial smooth has had its moment, snap out any residual drift so we
    // don't restart a fresh smooth animation every frame (which never arrives).
    if (now - start > SMOOTH_GRACE_MS && drift > DRIFT_TOLERANCE) {
      window.scrollTo({ top: targetTop(), behavior: 'auto' });
    }

    if (drift <= DRIFT_TOLERANCE) {
      if (!stableSince) stableSince = now;
    } else {
      stableSince = 0;
    }

    const settled = stableSince && now - stableSince > STABLE_HOLD_MS;
    if (now - start < maxSettleMs && !settled) {
      raf = requestAnimationFrame(tick);
    } else {
      cleanup();
    }
  };
  raf = requestAnimationFrame(tick);
}
